import cloneDeep from 'lodash/cloneDeep';

export const SOURCE_KINDS = Object.freeze({
    PAGE: 'page',
    FEATURE: 'feature',
    TRACK_TYPE: 'trackType',
    APPS: 'apps',
    PRODUCT_AREAS: 'productAreas',
    VISITORS: 'visitors',
    ACCOUNTS: 'accounts',
    WEB_ANALYTICS: 'webAnalytics',
    SURVEY: 'survey',
});

// Entity identity, never stored: renderers derive it from source.kind, so filter replacement can't drop it.
export const ACCOUNTS_SOURCE_SCOPE_FILTER = 'accountId != ""';

const ENTITY_SOURCE_KINDS = new Set([SOURCE_KINDS.PAGE, SOURCE_KINDS.FEATURE, SOURCE_KINDS.TRACK_TYPE]);

// Kinds whose descriptor is meaningless without an id — entity detail pages and a specific
// survey. (Survey is not an "entity" kind, but its link-back route still needs the id.)
const ID_REQUIRED_SOURCE_KINDS = new Set([...ENTITY_SOURCE_KINDS, SOURCE_KINDS.SURVEY]);

export const isEntitySourceKind = (kind) => ENTITY_SOURCE_KINDS.has(kind);

export function isValidSource (source) {
    if (!source || typeof source !== 'object') return false;
    if (!Object.values(SOURCE_KINDS).includes(source.kind)) return false;
    if (ID_REQUIRED_SOURCE_KINDS.has(source.kind)) return !!source.id;

    return true;
}

/**
 * FROZEN CONTRACT (APP-165060): widget definitions persist unversioned, so shapes produced
 * here must stay readable forever. `definition.source` is a structured { kind, id? } descriptor
 * (see SOURCE_KINDS), never a raw href. Shared page-state fields use fixed names: appId (array),
 * segmentId, dateRange, filters, columns, compare, search.
 * Deep-clones `definition`; throws on bad input — guard before calling from computeds.
 */
export function buildWidget ({ type, name, definition = {}, source }) {
    if (!type) {
        throw new Error('buildWidget requires a widget type');
    }
    if (source && !isValidSource(source)) {
        throw new Error(`buildWidget received an invalid source descriptor: ${JSON.stringify(source)}`);
    }

    const builtDefinition = cloneDeep(definition);
    Object.keys(builtDefinition).forEach((key) => {
        if (builtDefinition[key] === undefined) delete builtDefinition[key];
    });
    if (source) {
        builtDefinition.source = source.id ? { kind: source.kind, id: source.id } : { kind: source.kind };
    }

    return { type, name, definition: builtDefinition };
}
